import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Input from 'antd/lib/input';
import Menu from 'antd/lib/menu';
import notification from 'antd/lib/notification';
import Spin from 'antd/lib/spin';
import Table from 'antd/lib/table';
import moment from 'moment';
import {
  getEventDetail,
  getEventsList,
  getIncidentData,
  getCustomIncidentData,
  getAlertResolutionsList,
  updateAlertResolutions,
  postComment,
  snoozeAlert,
} from 'parity-network-requests';
import * as util from '../../../../utils/urlParams';
import { config } from '../../../../config';
import { CustomSnoozeModal } from '../../../CustomSnoozeModal';
import { CustomResolutionModal } from '../CustomResolutionModal';
import { AdditionalPointsModal } from './components/AdditionalPointsModal';
import { AlertsChart } from './components/AlertsChart';

// ICONS
import arrowDownBlue from '../../../../icons/arrow-down-blue.svg';
import arrowDownGrey from '../../../../icons/arrow-down-grey.svg';
import arrowDownWhite from '../../../../icons/arrow-down-white.svg';
import backImg from '../../../../icons/back.svg';
import dropdownArrowGrey from '../../../../icons/dropdown-grey.svg';
import dropdownArrowLightGrey from '../../../../icons/dropdown-light-grey.svg';
import snoozeIconGrey from '../../../../icons/snooze-grey.svg';
import snoozeIconWhite from '../../../../icons/snooze-white.svg';
import { CheckmarkIcon } from '../CheckmarkIcon';

// IMAGES
import editIcon from '../../../../images/edit.png';
import logo from '../../../../images/parity-logo-dark.png';

import './AlertDetails.less';

const { TextArea } = Input;

const { REACT_APP_BUILD_ENV } = config;

export const AlertDetails = (props) => {
  const { theme, buildingName } = props;
  const history = useHistory();
  const { search } = useLocation();
  const { building } = useParams();
  const { incident_id, event_id, data_points } = queryString.parse(search);
  const [event, setEvent] = useState({});
  const [events, setEvents] = useState([]);
  const [graphData, setGraphData] = useState({});
  const [defaultDataPoints, setDefaultDataPoints] = useState([]);
  const [additionalDataPoints, setAdditionalDataPoints] = useState([]);
  const [selectedAdditionalDataPoints, setSelectedAdditionalDataPoints] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resolutionOptions, setResolutionOptions] = useState([]);
  const [isCustomSnoozeModalOpen, setIsCustomSnoozeModalOpen] = useState(false);
  const [isCustomResolutionModalOpen, setIsCustomResolutionModalOpen] = useState(false);
  const [isAdditionalPointsModalOpen, setIsAdditionalPointsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const themedSnoozeIcon = theme === 'light' ? snoozeIconGrey : snoozeIconWhite;
  const themedArrowDown = theme === 'light' ? arrowDownGrey : arrowDownWhite;

  const addSystemTimings = (eventData) => {
    if (eventData.is_resolved) {
      eventData.comments.push({
        comment:
          ' has identified that the alert conditions have returned to their required ranges. This alert has been cleared.',
        created: eventData.end_timestamp,
        user: 'Pi',
      });
    }
    eventData.comments.push({
      comment: ' identified this alert.',
      created: eventData.start_timestamp,
      user: 'Pi',
    });
    const commentsFromApi = eventData.comments.sort((a, b) => moment(b.created) - moment(a.created));
    setComments(commentsFromApi);
  };

  const setAlertLineColorObj = (conditions, additionalPoints) => {
    const tempColorObj = {};

    const lineColors = [
      '#BA9762',
      '#C15EAF',
      '#2FB2AD',
      '#FF8246',
      '#F05E98',
      '#6081EE',
      '#DF4949',
      '#F7CC00',
      '#1A87B9',
      '#5CB91A',
      '#9b603e',
      '#82C6D7',
      '#F2BDDE',
      '#4A1BA4',
      '#B0CB1D',
    ];

    conditions.map((dataPoint) => {
      tempColorObj[dataPoint.data_point_tag] = lineColors.shift();
      return tempColorObj;
    });

    additionalPoints.map((dataPoint) => {
      tempColorObj[dataPoint.tag] = lineColors.shift();
      return tempColorObj;
    });

    sessionStorage.setItem('alertLineColorObj', JSON.stringify(tempColorObj));
  };

  useEffect(() => {
    async function fetchData() {
      const eventDetails = await getEventDetail(REACT_APP_BUILD_ENV, building, incident_id, event_id);

      const dataPointsFromApi = await getIncidentData(REACT_APP_BUILD_ENV, eventDetails.context_api_location);

      const defaultDataPointsFromApi = dataPointsFromApi.data_points.filter(
        (dataPoint) => dataPoint.is_default === true,
      );
      const additionalDataPointsFromApi = dataPointsFromApi.data_points.filter(
        (dataPoint) => dataPoint.is_default === false,
      );

      let graphDataFromApi = {};

      if (data_points) {
        // Set the request end time to end timestamp for resolved alerts and last seen for active
        let endTimestamp = eventDetails.end_timestamp
          ? moment(eventDetails.end_timestamp).add(3, 'hours').utc().format()
          : moment(eventDetails.last_seen_timestamp).add(3, 'hours').utc().format();

        // Request the graph data until current time if the event happened less than 3 hours ago
        const currentTime = moment().utc().format();
        if (endTimestamp > currentTime) endTimestamp = currentTime;

        // Request the custom graph data with all the datapoints included
        graphDataFromApi = await getCustomIncidentData(
          REACT_APP_BUILD_ENV,
          building,
          data_points,
          eventDetails.start_timestamp,
          endTimestamp,
        );

        // Filter the selected additional data points
        const currentAdditionalDataPoints = [];
        const dataPointsArray = data_points.split(',');
        dataPointsArray.forEach((dataPoint) => {
          additionalDataPointsFromApi.forEach((additionalDataPoint) => {
            if (additionalDataPoint.tag === dataPoint) currentAdditionalDataPoints.push(additionalDataPoint);
          });
        });
        setSelectedAdditionalDataPoints(currentAdditionalDataPoints);
        await setAlertLineColorObj(eventDetails.condition_failure, currentAdditionalDataPoints);
      } else {
        graphDataFromApi = await getIncidentData(REACT_APP_BUILD_ENV, eventDetails.context_data_api_location);
        const conditions = [];
        graphDataFromApi.data_points.map((dataPoint) => {
          conditions.push({ data_point_tag: dataPoint.tag });
          return dataPoint.tag;
        });
        setSelectedAdditionalDataPoints([]);
        await setAlertLineColorObj(conditions, []);
      }

      const eventsFromApi = await getEventsList(REACT_APP_BUILD_ENV, building);
      const resolutionOptionsFromApi = await getAlertResolutionsList(REACT_APP_BUILD_ENV, false);

      window.document.title = `${buildingName} | Alert Details`;

      setEvent(eventDetails);
      addSystemTimings(eventDetails);
      setDefaultDataPoints(defaultDataPointsFromApi);
      setAdditionalDataPoints(additionalDataPointsFromApi);
      setGraphData(graphDataFromApi);
      setEvents(eventsFromApi.results);
      setResolutionOptions(resolutionOptionsFromApi);
      setIsLoading(false);
    }
    fetchData();
  }, [building, incident_id, event_id, data_points]);

  const onSnoozeButtonPress = async (alert) => {
    const { alert_uid_hash } = alert;
    let response;

    if (alert.snooze_context.is_snoozed) {
      response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, 0);

      if (response === true) {
        // Handle success
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert has been unsnoozed`,
          description: `You will resume receiving notifications.`,
        });
      } else {
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert could not be unsnoozed`,
          description: `We encountered an error while trying to unsnooze this alert. Please try again.`,
        });
      }
    } else {
      response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, 3);

      if (response === true) {
        // Handle success
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert has been snoozed`,
          description: `You will resume receiving notifications when you unsnooze it.`,
        });
      } else {
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert could not be snoozed`,
          description: `We encountered an error while trying to snooze this alert. Please try again.`,
        });
      }
    }

    const eventDetails = await getEventDetail(REACT_APP_BUILD_ENV, building, incident_id, event_id);
    setEvent(eventDetails);
  };

  const submitSnoozeAlert = async (hours) => {
    const { alert_uid_hash } = event;

    setIsCustomSnoozeModalOpen(false);
    const response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, hours);

    if (response === true) {
      // Handle success
      notification.info({
        className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
        placement: 'topRight',
        duration: 5,
        icon: <CheckmarkIcon theme={theme} />,
        message: `This alert has been snoozed`,
        description: `You will resume receiving notifications from it starting ${moment()
          .add(hours, 'hours')
          .format('LLL')}`,
      });
    } else {
      notification.info({
        className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
        placement: 'topRight',
        duration: 5,
        icon: <CheckmarkIcon theme={theme} />,
        message: `This alert could not be snoozed`,
        description: `We encountered an error while trying to snooze this alert. Please try again.`,
      });
    }

    const eventDetails = await getEventDetail(REACT_APP_BUILD_ENV, building, incident_id, event_id);
    setEvent(eventDetails);
  };

  const addStandardResolution = async (resolutionName) => {
    const { resolutions } = event;
    const updatedResolutions = resolutions.filter((resolution) => resolution.name !== resolutionName);
    updatedResolutions.push({ name: resolutionName, slug: null, is_removed: false });

    const response = await updateAlertResolutions(
      REACT_APP_BUILD_ENV,
      building,
      incident_id,
      event.uid_hash,
      updatedResolutions,
    );

    if (response === true) {
      // Handle success
      notification.info({
        className: `alerts-resolution-notification alerts-resolution-notification-${theme}`,
        placement: 'topRight',
        duration: 5,
        icon: <CheckmarkIcon theme={theme} />,
        message: 'Resolution successfully added!',
      });

      const eventDetails = await getEventDetail(REACT_APP_BUILD_ENV, building, incident_id, event_id);
      setEvent(eventDetails);
    } else {
      notification.info({
        className: `alerts-resolution-notification alerts-resolution-notification-${theme}`,
        placement: 'topRight',
        duration: 5,
        icon: <CheckmarkIcon theme={theme} />,
        message: 'We encountered an error while trying to add this resolution. Please try again.',
      });
    }
  };

  const onCustomResolutionSubmit = async () => {
    const eventDetails = await getEventDetail(REACT_APP_BUILD_ENV, building, incident_id, event_id);
    setEvent(eventDetails);
    setIsCustomResolutionModalOpen(false);
  };

  const applyUpdatedDataPoints = async (dataPointTags) => {
    // Set the request end time to end timestamp for resolved alerts and last seen for active
    let endTimestamp = event.end_timestamp
      ? moment(event.end_timestamp).add(3, 'hours').utc().format()
      : moment(event.last_seen_timestamp).add(3, 'hours').utc().format();

    // Request the custom graph data with all the datapoints included
    const currentTime = moment().utc().format();
    if (endTimestamp > currentTime) endTimestamp = currentTime;

    // Request the custom graph data with all the datapoints included
    const requestedDataPoints = dataPointTags.join();
    const graphDataFromApi = await getCustomIncidentData(
      REACT_APP_BUILD_ENV,
      building,
      requestedDataPoints,
      event.start_timestamp,
      endTimestamp,
    );

    // Filter the selected additional data points
    const updatedAdditionalDataPoints = [];
    dataPointTags.forEach((dataPointTag) => {
      additionalDataPoints.forEach((additionalDataPoint) => {
        if (additionalDataPoint.tag === dataPointTag) updatedAdditionalDataPoints.push(additionalDataPoint);
      });
    });

    // Update the URL with the additional datapoints
    const dataPointParams = util.addSearchParams(dataPointTags);
    history.replace(`/alerts/${building}?incident_id=${incident_id}&event_id=${event_id}${dataPointParams}`);

    const conditions = [];
    graphDataFromApi.data_points.map((dataPoint) => {
      conditions.push({ data_point_tag: dataPoint.tag });
      return dataPoint.tag;
    });
    await setAlertLineColorObj(conditions, []);

    setSelectedAdditionalDataPoints(updatedAdditionalDataPoints);
    setGraphData(graphDataFromApi);
    setIsAdditionalPointsModalOpen(false);
  };

  const submitComment = async () => {
    if (!newComment) return;

    setSubmitting(true);
    await postComment(REACT_APP_BUILD_ENV, event.comments_api_location, newComment);

    const eventDetails = await getEventDetail(REACT_APP_BUILD_ENV, building, incident_id, event_id);
    setEvent(eventDetails);

    addSystemTimings(eventDetails);

    setNewComment('');
    setSubmitting(false);
  };

  const getSeverityColour = () => {
    if (event.severity === 'high') return '#EA856F';
    if (event.severity === 'medium') return '#DEC802';
    return '#796FEA';
  };

  const getDropdownIcon = () => {
    return theme === 'light' ? (
      <img src={dropdownArrowGrey} alt="dropdown" />
    ) : (
      <img src={dropdownArrowLightGrey} alt="dropdown" />
    );
  };

  const snoozeMenu = (
    <Menu className={`alerts-snooze-menu app-dropdown-menu app-dropdown-menu-${theme}`}>
      <Menu.Item key="0" disabled>
        <div role="presentation">Snooze for...</div>
      </Menu.Item>
      <Menu.Item key="1">
        <div role="presentation" onClick={() => submitSnoozeAlert(3)}>
          3 hours
        </div>
      </Menu.Item>
      <Menu.Item key="2">
        <div role="presentation" onClick={() => submitSnoozeAlert(6)}>
          6 hours
        </div>
      </Menu.Item>
      <Menu.Item key="3">
        <div role="presentation" onClick={() => submitSnoozeAlert(12)}>
          12 hours
        </div>
      </Menu.Item>
      <Menu.Item key="4">
        <div role="presentation" onClick={() => submitSnoozeAlert(24)}>
          24 hours
        </div>
      </Menu.Item>
      <Menu.Item key="5">
        <div role="presentation" onClick={() => setIsCustomSnoozeModalOpen(true)}>
          Custom
        </div>
      </Menu.Item>
    </Menu>
  );

  const resolutionMenu = (
    <Menu className={`alerts-resolution-menu app-dropdown-menu app-dropdown-menu-${theme}`}>
      <Menu.Item key="0" disabled>
        <div role="presentation">Select Resolution</div>
      </Menu.Item>
      {resolutionOptions.map((resolutionOption) => {
        return (
          <Menu.Item key={resolutionOption.name}>
            <div role="presentation" onClick={() => addStandardResolution(resolutionOption.name)}>
              {resolutionOption.name}
            </div>
          </Menu.Item>
        );
      })}
      <Menu.Item key="Custom resolution">
        <div role="presentation" onClick={() => setIsCustomResolutionModalOpen(true)}>
          Custom
        </div>
      </Menu.Item>
    </Menu>
  );

  const dataPointsColumns = [
    {
      title: 'ADDITIONAL POINTS',
      dataIndex: ['display_name', 'tag'],
      render: (displayName, dataPoint) => {
        return (
          <div>
            <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <div
                id="alert-line-width"
                style={{
                  backgroundColor: `${JSON.parse(sessionStorage.getItem('alertLineColorObj'))[dataPoint.tag]}`,
                }}
              />
              {dataPoint.display_name}
            </span>
            <div className="alerts-details-datapoints-table-container mobile">
              <div className="alerts-details-datapoints-table-row">
                <span>Data Tag:</span>
                <span>{dataPoint.tag}</span>
              </div>
              <div className="alerts-details-datapoints-table-row">
                <span>Latest Reading:</span>
                <span>{dataPoint.status}</span>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'DATA TAG',
      dataIndex: 'tag',
      className: 'desktop',
    },
    {
      title: 'LATEST READING',
      dataIndex: 'status',
      className: 'desktop',
    },
  ];

  const renderStatusDescription = () => {
    if (event.snooze_context.is_snoozed) {
      return (
        <strong className="alerts-details-status-description-snoozed">
          {`Snoozed until ${moment(event.snooze_context.snoozed_until).format('MMM D, YYYY (LT)')}`}
        </strong>
      );
    }
    if (event.is_resolved) {
      return (
        <div>
          <strong className="alerts-details-status-description-cleared">Alert Cleared: </strong>
          <span className="alerts-details-status-description-time">
            {moment(event.end_timestamp).format('MMM D, YYYY (LT)')}
          </span>
        </div>
      );
    }
    return <strong>Alert Detected:</strong>;
  };

  const renderStatusButtons = () => {
    if (event.is_resolved) {
      if (event.resolutions.length > 0) {
        return (
          <div className="alerts-details-status-resolutions">
            <div className="alerts-details-status-resolutions-list">
              {event.resolutions.map((resolution) => {
                return <span key={resolution.name}>{resolution.name}</span>;
              })}
            </div>
            <Dropdown overlay={resolutionMenu} trigger={['click']}>
              <div className="alerts-details-status-resolutions-edit">
                <img className="alerts-details-status-resolutions-edit-icon" src={editIcon} alt="edit" />
              </div>
            </Dropdown>
          </div>
        );
      }
      return (
        <Dropdown overlay={resolutionMenu} trigger={['click']}>
          <Button className="alerts-details-status-resolutions-add">
            <strong>Add Resolution</strong>
            <img src={arrowDownBlue} alt="arrow-down" />
          </Button>
        </Dropdown>
      );
    }

    return (
      <div className="alerts-details-status-snooze-container">
        {event.snooze_context.is_snoozed ? (
          <Button
            className={`snooze-component snooze-component-snoozed snooze-component-${theme}`}
            onClick={() => onSnoozeButtonPress(event)}
          >
            Unsnooze
          </Button>
        ) : (
          <>
            <Button
              className={`snooze-component snooze-component-unsnoozed snooze-component-${theme}`}
              onClick={() => onSnoozeButtonPress(event)}
            >
              <img src={themedSnoozeIcon} alt="snooze" />
            </Button>
            <Dropdown overlay={snoozeMenu} trigger={['click']}>
              <Button className={`dropdown-component dropdown-component-${theme}`}>
                <img src={themedArrowDown} alt="dropdown" />
              </Button>
            </Dropdown>
          </>
        )}
      </div>
    );
  };

  const getEventDuration = () => {
    const duration = moment.duration(moment(event.latest_failed_timestamp).diff(moment(event.start_timestamp)));

    const durationInMinutes = duration.asMinutes().toFixed(0);
    if (durationInMinutes <= 60) return `${durationInMinutes} minutes`;

    const durationInHours = duration.asHours().toFixed(0);
    if (durationInHours <= 24) return `${durationInHours} hours`;

    const durationInDays = duration.asDays().toFixed(0);
    return `${durationInDays} days`;
  };

  const getEventLogCount = () => (event.is_resolved ? 2 + event.comments_count : 1 + event.comments_count);

  const renderAlertDetails = () => {
    return (
      <>
        <div className="alerts-details-back-container">
          <div
            className="alerts-details-back"
            role="button"
            onClick={() => (history.length > 2 ? history.goBack() : history.push(`/alerts/${building}?tab=Overview`))}
            onKeyDown={() => (history.length > 2 ? history.goBack() : history.push(`/alerts/${building}?tab=Overview`))}
            tabIndex={0}
          >
            <img src={backImg} alt="back" className="alerts-details-back-image" />
            Back
          </div>
          {!isLoading && (
            <div className="alerts-details-header-seen-container mobile">
              <span>Seen:</span>
              <Avatar.Group className="alerts-details-header-seen-users">
                {event.seen_by.map((user) => {
                  const colour = user.avatar_color || '#ffffff';
                  const firstAndLastNames = user.name.split(' ');
                  return (
                    <Avatar
                      key={`${firstAndLastNames[0][0]}${firstAndLastNames[1][0]}`}
                      className="app-profile-icon"
                      alt="profile icon"
                      style={{ backgroundColor: colour }}
                    >
                      {`${firstAndLastNames[0][0]}${firstAndLastNames[1][0]}`}
                    </Avatar>
                  );
                })}
              </Avatar.Group>
            </div>
          )}
        </div>
        {isLoading ? (
          <div className="alerts-details-header-loading">
            <Spin />
          </div>
        ) : (
          <div className="alerts-details-header">
            <Dropdown
              className="alerts-details-header-select"
              trigger={['click']}
              overlay={
                <Menu className={`app-dropdown-menu app-dropdown-menu-${theme}`}>
                  {events.map((alert) => {
                    const incidentId = alert.incident_uid_hash;
                    const eventId = alert.uid_hash;
                    return (
                      <Menu.Item
                        key={alert.uid_hash}
                        onClick={() => {
                          history.replace(
                            `/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}&data_points=${data_points}`,
                          );
                        }}
                        onKeyDown={() => {
                          history.replace(
                            `/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}&data_points=${data_points}`,
                          );
                        }}
                        role="link"
                        tabIndex={0}
                      >
                        {moment(alert.start_timestamp).format('MMM D, YYYY (LT)')}
                      </Menu.Item>
                    );
                  })}
                </Menu>
              }
            >
              <div>
                <span>Identified:</span>
                <strong>{moment(event.start_timestamp).format('MMM D, YYYY (LT)')}</strong>
                {getDropdownIcon()}
              </div>
            </Dropdown>
            <div className="alerts-details-header-seen-container desktop">
              <span>Seen:</span>
              <Avatar.Group className="alerts-details-header-seen-users">
                {event.seen_by.map((user) => {
                  const colour = user.avatar_color || '#ffffff';
                  const firstAndLastNames = user.name.split(' ');
                  return (
                    <Avatar
                      key={`${firstAndLastNames[0][0]}${firstAndLastNames[1][0]}`}
                      className="app-profile-icon"
                      alt="profile icon"
                      style={{ backgroundColor: colour }}
                    >
                      {`${firstAndLastNames[0][0]}${firstAndLastNames[1][0]}`}
                    </Avatar>
                  );
                })}
              </Avatar.Group>
            </div>
          </div>
        )}
        <div className="alerts-details-row">
          <div className="alerts-details-main-card">
            {isLoading ? (
              <div className="alerts-details-row-loading">
                <Spin />
              </div>
            ) : (
              <>
                <div className="alerts-details-status-container">
                  <div className="alerts-details-status-colour" style={{ borderLeftColor: getSeverityColour() }} />
                  <div className="alerts-details-status-contents">
                    {renderStatusDescription()}
                    <strong className="alerts-details-status-title">{event.name}</strong>
                    <span className="alerts-details-status-tier">
                      {`Tier: ${event.severity.charAt(0).toUpperCase() + event.severity.slice(1)}`}
                    </span>
                    {renderStatusButtons()}
                  </div>
                </div>
                <div className="alerts-details-info-container">
                  <div className="alerts-details-info-header">DETAILS</div>
                  <div className="alerts-details-info-body">
                    {event.description && (
                      <div className="alerts-details-info-item">
                        <span className="alerts-details-info-name">Alert:</span>
                        <div className="alerts-details-info-description">{event.description}</div>
                      </div>
                    )}
                    {/* <div className="alerts-details-info-item">
                      <span className="alerts-details-info-name">Potential Impact:</span>
                      <div className="alerts-details-info-description">Resident Comfort</div>
                    </div> */}
                    <div className="alerts-details-info-item">
                      <span className="alerts-details-info-name">System:</span>
                      <div className="alerts-details-info-description">{event.hvac_system_display}</div>
                    </div>
                    <div className="alerts-details-info-item">
                      <span className="alerts-details-info-name">Conditions:</span>
                      <div className="alerts-details-info-description">
                        {event.condition_failure.map((condition) => {
                          const lineColorObj = JSON.parse(sessionStorage.getItem('alertLineColorObj'));

                          return (
                            <div className="condition-line">
                              <div
                                id="alert-line-width"
                                style={{ backgroundColor: `${lineColorObj[condition.data_point_tag]}` }}
                              />
                              <span>{`${condition.message}`}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="alerts-details-info-item alerts-details-info-item-duration">
                      <span className="alerts-details-info-name">Duration:</span>
                      <div className="alerts-details-info-description">
                        <span className="alerts-details-info-duration">{getEventDuration()}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="alerts-details-graph-section">
                  <div className="alerts-details-graph-section-header">
                    <Dropdown
                      className="alerts-details-graph-event-dropdown"
                      trigger={['click']}
                      overlay={
                        <Menu className={`app-dropdown-menu app-dropdown-menu-${theme}`}>
                          {events.map((alert) => {
                            const incidentId = alert.incident_uid_hash;
                            const eventId = alert.uid_hash;
                            return (
                              <Menu.Item
                                key={alert.uid_hash}
                                onClick={() => {
                                  history.replace(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`);
                                }}
                                onKeyDown={() => {
                                  history.replace(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`);
                                }}
                                role="link"
                                tabIndex={0}
                              >
                                {moment(alert.start_timestamp).format('MMM D, YYYY (LT)')}
                              </Menu.Item>
                            );
                          })}
                        </Menu>
                      }
                    >
                      <div>
                        <span>Identified:</span>
                        <span className="alerts-details-graph-event-dropdown-time">
                          {moment(event.start_timestamp).format('MMM D, YYYY (LT)')}
                        </span>
                        {getDropdownIcon()}
                      </div>
                    </Dropdown>
                  </div>
                  <div className="alerts-details-graph">
                    <AlertsChart data={graphData} theme={theme} />
                  </div>
                  <Button className="alerts-details-graph-button" onClick={() => setIsAdditionalPointsModalOpen(true)}>
                    Compare Additional Points
                  </Button>
                  {selectedAdditionalDataPoints.length > 0 ? (
                    <Table
                      className="alerts-details-datapoints-table"
                      columns={dataPointsColumns}
                      dataSource={selectedAdditionalDataPoints}
                      pagination={false}
                    />
                  ) : (
                    <span className="alerts-details-graph-section-description">
                      Comparing additional points can help you see if other data points correlate to the issue
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="alerts-details-event-log">
            {isLoading ? (
              <div className="alerts-details-row-loading">
                <Spin />
              </div>
            ) : (
              <>
                <h2 className="alerts-details-event-log-header">{`Event Log (${getEventLogCount()})`}</h2>
                <div className="alerts-details-event-log-add">
                  <strong>Add a note to keep your team informed</strong>
                  <TextArea
                    rows={4}
                    onChange={(e) => setNewComment(e.target.value)}
                    value={newComment}
                    placeholder="Specify the maintenance being performed"
                  />
                  <div className="alerts-details-event-log-button-wrapper">
                    <Button className="alerts-details-event-log-button" loading={submitting} onClick={submitComment}>
                      Post Note
                    </Button>
                  </div>
                </div>
                <ul>
                  {comments.map((comment) => {
                    const colour = comment.user_avatar_color || '#ffffff';
                    const firstAndLastNames = comment.user.split(' ');
                    return (
                      <li className="alerts-details-event-log-item">
                        <div className="alerts-details-event-log-profile-column">
                          {comment.user === 'Pi' ? (
                            <Avatar
                              className="app-profile-icon alerts-details-event-log-logo"
                              src={logo}
                              alt="Parity logo"
                            >
                              Pi
                            </Avatar>
                          ) : (
                            <Avatar className="app-profile-icon" alt="profile icon" style={{ backgroundColor: colour }}>
                              {`${firstAndLastNames[0][0]}${firstAndLastNames[1][0]}`}
                            </Avatar>
                          )}
                        </div>
                        <div className="alerts-details-event-log-text-column">
                          <div className="alerts-details-event-log-item-text">
                            <strong>{comment.user}</strong>
                            {comment.user === 'Pi' ? (
                              <span>{comment.comment}</span>
                            ) : (
                              <span>{` says: ${comment.comment}`}</span>
                            )}
                          </div>
                          <span className="alerts-details-event-log-item-time">
                            {`${moment(comment.created).fromNow()} • ${moment(comment.created).format(
                              'MMM D, YYYY (LT)',
                            )}`}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={`alerts-details-wrapper alerts-details-wrapper-${theme}`}>
      {renderAlertDetails()}
      {isCustomSnoozeModalOpen && (
        <CustomSnoozeModal
          onCancel={() => setIsCustomSnoozeModalOpen(false)}
          onSubmit={submitSnoozeAlert}
          theme={theme}
        />
      )}
      {isCustomResolutionModalOpen && (
        <CustomResolutionModal
          alertForResolution={event}
          resolutionOptions={resolutionOptions}
          onCancel={() => setIsCustomResolutionModalOpen(false)}
          onSubmit={onCustomResolutionSubmit}
          theme={theme}
        />
      )}
      {isAdditionalPointsModalOpen && (
        <AdditionalPointsModal
          defaultDataPoints={defaultDataPoints}
          additionalDataPoints={additionalDataPoints}
          currentAdditionalDataPoints={selectedAdditionalDataPoints}
          saveChanges={applyUpdatedDataPoints}
          onCancel={() => setIsAdditionalPointsModalOpen(false)}
          theme={theme}
        />
      )}
    </div>
  );
};
